import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { NodeKind } from '@/shared/api/generated/lineage';

import { KIND_COLOUR } from '../model/graph';

/* What the dots mean, and what the focus is doing.
 *
 * **The kinds are the platform's three, not the reference's three.** The design draws a legend of
 * *Source · Dataset · Report*; `NodeKind` is `dataset | column | job`. Rendering the design's words
 * would put a vocabulary on screen that no API field, no filter and no other screen uses — a reader
 * who then went looking for "Report" anywhere else in the product would not find it. That is the
 * `LifecycleStrip` decision at a second site: the spec asked for five rule states and the API has
 * three, and inventing the two would have been the more faithful-looking and less honest answer.
 *
 * **Derived from `KIND_COLOUR`, so the legend cannot disagree with the graph.** A hand-written swatch
 * list is how a legend comes to describe last month's palette; `KIND_COLOUR` is
 * `satisfies Record<NodeKind, string>`, so a fourth kind added to the contract appears here without
 * anybody remembering to add it.
 *
 * The focus sentence is on the right and is the one thing on this row that changes: it names the node
 * and states what the dimming means. Without it, a graph with three of six boxes faded reads as a
 * rendering fault rather than as an answer.
 */
export function LineageLegend({
  focused,
}: {
  /** The node the graph is centred on, or `null` before anything is selected. */
  readonly focused: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const kinds = Object.keys(KIND_COLOUR) as readonly NodeKind[];

  return (
    <div className="lq-legend">
      {kinds.map((kind) => (
        <span className="lq-legend__item" key={kind}>
          <span
            className="lq-legend__dot"
            style={{ background: KIND_COLOUR[kind] }}
            aria-hidden="true"
          />
          {t(`lineage.kind.${kind}`)}
        </span>
      ))}
      <span className="lq-legend__focus">
        {focused === null
          ? t('lineage.legend.unfocused')
          : t('lineage.legend.focused', { name: focused })}
      </span>
    </div>
  );
}
